import { displayDateTimeToMinute, Entity, FieldType, FileStorageType, PersistenceType } from 'gossamer-unified';
import { Event } from './Event';
import { Location } from './Location';
import { UserView } from './UserView';
import { TicketBooking } from './TicketBooking';
import { AbsenceReasonOptions, AttendanceResultOptions } from './SharedOptions';
import { EVENTS_I_AM_ADMIN_OF, EVENTS_I_HOST } from './SecurityRules';

export interface AttendanceRow {
  id: number;
  user_id: string;
  event_id: number;
  ticket_booking_id: number;
  recording_method: string;
  signature: string;
  signature_name: string;
  attendance_result: string;
  absence_reason: string;
  user_comments: string;
  gps_status: string;
  gps_present: boolean | null;
  gps_location: number | null;
  flags: string;
  flag_reasons: string;
  created_at: string;
  event_start_at: string;
  event_end_at: string;
}

export const Attendance: Entity<AttendanceRow> = {
  id: 'Attendance',
  title: 'Attendance',
  service: 'main',
  rowLabel: (row: AttendanceRow) => [row.user_id, row.event_id].join(' / '),
  rowLabelDynamic: (row: AttendanceRow) => `<lookup:UserView:${row.user_id}> / <lookup:EventView:${row.event_id}>`,
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.attendance',
  },
  security: [
    {
      role: 'user',
      action: 'read',
      access: 'conditional',
      condition: `event_id IN ${EVENTS_I_HOST} OR user_id = '{userId}'`,
    },
    {
      role: 'admin',
      action: 'all',
      access: 'conditional',
      condition: `event_id IN ${EVENTS_I_AM_ADMIN_OF}`,
    },
    { role: 'staff', action: 'all', access: 'all' },
  ],
  listRowLink: '/admin/attendance/display/{key}',
  fields: {
    id: {
      seq: 5,
      type: FieldType.Integer,
      label: 'Id',
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      autoIncrement: true,
    },
    user_id: {
      seq: 10,
      type: FieldType.Reference,
      label: 'User',
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      listColumn: true,
      refEntity: UserView,
    },
    event_id: {
      seq: 15,
      label: 'Event',
      type: FieldType.Reference,
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
      refEntity: Event,
    },
    ticket_booking_id: {
      seq: 20,
      label: 'Booking',
      persistence: PersistenceType.BackEndManaged,
      type: FieldType.Reference,
      refEntity: TicketBooking,
    },
    recording_method: {
      seq: 25,
      label: 'Type',
      type: FieldType.Options,
      listColumn: true,
      mandatory: true,
      options: [
        {
          id: 'qr_code',
          label: 'QR Code',
        },
        {
          id: 'e_sig',
          label: 'eSignature',
        },
        {
          id: 'manual',
          label: 'manually added',
        },
        {
          id: 'auto_playback',
          label: 'Auto playback',
        },
        {
          id: 'auto_livestream',
          label: 'Auto livestream',
        },
      ],
    },
    signature: {
      seq: 30,
      label: 'Signature',
      type: FieldType.File,
      imagesOnly: true,
      storageType: FileStorageType.DataURL,
    },
    signature_name: {
      seq: 35,
      label: 'Signature Name',
      type: FieldType.Text,
    },
    attendance_result: {
      seq: 40,
      label: 'Attendance Result',
      type: FieldType.Options,
      mandatory: true,
      listColumn: true,
      options: AttendanceResultOptions,
    },
    absence_reason: {
      seq: 45,
      label: 'Absence Reason',
      type: FieldType.Options,
      options: AbsenceReasonOptions,
    },
    user_comments: {
      seq: 50,
      label: 'User Comments',
      type: FieldType.Richtext,
    },
    gps_status: {
      seq: 55,
      label: 'GPS Status',
      type: FieldType.Options,
      persistence: PersistenceType.BackEndManaged,
      options: [
        {
          id: 'captured',
          label: 'captured',
        },
        {
          id: 'not_requested',
          label: 'not requested',
        },
        {
          id: 'permission_denied',
          label: 'permission denied',
        },
        {
          id: 'unavailable',
          label: 'unavailable',
        },
        {
          id: 'timed_out',
          label: 'timed out',
        },
        {
          id: 'error',
          label: 'error',
        },
      ],
    },
    gps_present: {
      seq: 60,
      label: 'GPS Present',
      type: FieldType.Boolean,
      persistence: PersistenceType.BackEndManaged,
      description: 'Whether GPS presence was captured during attendance recording',
    },
    gps_location: {
      seq: 65,
      label: 'GPS Location',
      type: FieldType.Reference,
      persistence: PersistenceType.BackEndManaged,
      refEntity: Location,
      description: 'Reference to GPS Location captured during attendance recording',
    },
    created_at: {
      seq: 70,
      label: 'Created At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      listColumn: true,
    },
    flags: {
      seq: 75,
      label: 'Flags',
      type: FieldType.Text,
      persistence: PersistenceType.FrontEndOnly,
      listColumn: true,
      evaluateFrontEndIn: (row: AttendanceRow) => {
        const out = [];
        if (row.created_at > row.event_end_at) {
          out.push('⌛');
        }
        if (row.recording_method === 'manual') {
          out.push('🔑');
        }
        if (!!row.user_comments) {
          out.push('🗨');
        }
        if (row.attendance_result === 'absent') {
          out.push('🚩');
        }
        if (row.attendance_result === 'noSession') {
          out.push('🚩');
        }
        if (row.gps_present === true) {
          out.push('🖈');
        }
        if (row.gps_present === false) {
          out.push('❌');
        }
        return out.join(' ');
      },
    },
    flag_reasons: {
      seq: 80,
      label: 'Flag Reasons',
      type: FieldType.Richtext,
      persistence: PersistenceType.FrontEndOnly,
      evaluateFrontEndIn: (row: AttendanceRow) => {
        const out = [];
        if (row.created_at > row.event_end_at) {
          out.push('recorded after event finished');
        }
        if (row.recording_method === 'manual') {
          out.push('added manually by an admin');
        }
        if (!!row.user_comments) {
          out.push('includes comments');
        }
        if (row.attendance_result !== 'attended') {
          out.push(row.attendance_result);
        }
        if (row.gps_present === true) {
          out.push('GPS tracked - at location');
        }
        if (row.gps_present === false) {
          out.push('GPS tracked - NOT at location');
        }
        return out.join('\n');
      },
    },
    event_start_at: {
      seq: 85,
      label: 'Event Started At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      getDisplayText: (value: string) => displayDateTimeToMinute(value, '-'),
    },
    event_end_at: {
      seq: 90,
      label: 'Event Ended At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      getDisplayText: (value: string) => displayDateTimeToMinute(value, '-'),
    },
  },
};
